import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Search, Plus, Users, FilePlus, Edit, UserPlus, Trash } from "lucide-react";
import { Department } from "@/types/department";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import AddEmployeeModal from "@/components/employee/AddEmployeeModal";
import CreateMFOModal from "@/components/mfo/CreateMFOModal";
import AddDivisionModal from "@/components/division/AddDivisionModal";
import { MFOItem } from "@/types/kanban";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { createDepartment, deleteDepartment, fetchDepartments, updateDepartment } from "@/utils/supabase/departmentUtils";
import { createEmployee } from "@/utils/supabase/employeeUtils";
import { createMFO } from "@/utils/supabase/mfoUtils";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface CreateMFOModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  departments: { id: string; name: string }[];
  employees: { id: string; name: string; avatar?: string }[];
  mfoCodes: { id: string; code: string; title: string }[];
  onMFOCreated?: (mfo: MFOItem) => Promise<void>;
}

const Departments = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isCreateMFOModalOpen, setIsCreateMFOModalOpen] = useState(false);
  const [isAddDivisionModalOpen, setIsAddDivisionModalOpen] = useState(false);
  const [isEditDivisionModalOpen, setIsEditDivisionModalOpen] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState<Department | null>(null);
  const [editedDivision, setEditedDivision] = useState({
    name: "",
    description: "",
    headName: "",
    headTitle: "",
    budget: 0,
    vision: "",
    mission: "",
    goals: [""]
  });
  const [allEmployees, setAllEmployees] = useState<{id: string, name: string, avatar?: string}[]>([]);
  const [mfoCodes, setMfoCodes] = useState<{id: string, code: string, title: string}[]>([]);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setIsLoading(true);
 
        const { data, error } = await supabase
          .from('departments')
          .select(`
            id, name, description, head_name, head_title, 
            budget, vision, mission, year, mfo_count,
            department_goals (goal)
          `);
          
        if (error) throw error;
        
        const formattedDepartments = data.map(dept => ({
          id: dept.id,
          name: dept.name,
          description: dept.description || "",
          headName: dept.head_name,
          headTitle: dept.head_title,
          budget: Number(dept.budget),
          vision: dept.vision || "",
          mission: dept.mission || "",
          year: dept.year,
          mfoCount: dept.mfo_count,
          goals: dept.department_goals?.map(g => g.goal) || [],
          collaborators: []
        }));
        
        for (const dept of formattedDepartments) {
          const { data: employees, error: employeeError } = await supabase
            .from('employees')
            .select('id, name, avatar')
            .eq('department_id', dept.id);
            
          if (!employeeError && employees) {
            dept.collaborators = employees;
          }
        }
        
        setDepartments(formattedDepartments);
        
      } catch (error) {
        console.error("Failed to load departments:", error);
        toast({
          title: "Error",
          description: "Failed to load departments",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartments();
  }, [toast]);
  
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data, error } = await supabase
          .from('employees')
          .select('id, name, avatar');
        
        if (error) throw error;
        
        if (data) {
          setAllEmployees(data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    
    const fetchMFOCodes = async () => {
      try {
        const { data, error } = await supabase
          .from('mfo_items')
          .select('code, title')
          .order('code');
        
        if (error) throw error;
        
        if (data) {
          const uniqueCodes = new Map();
          
          data.forEach(item => {
            const codeParts = item.code.split(' ');
            if (codeParts?.length >= 2) {
              const baseCode = `${codeParts[0]} ${codeParts[1].charAt(0)}`;
              const codeNum = codeParts[1].charAt(0);
              
              if (!uniqueCodes.has(codeNum)) {
                uniqueCodes.set(codeNum, {
                  id: `mfo${codeNum}`,
                  code: baseCode,
                  title: item.title.split(' ').slice(0, 3).join(' ')
                });
              }
            }
          });
          
          setMfoCodes(Array.from(uniqueCodes.values()));
        }
      } catch (error) {
        console.error("Error fetching MFO codes:", error);
      }
    };
    
    fetchEmployees();
    fetchMFOCodes();
  }, []);

  const filteredDepartments = departments.filter(dept => 
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddDivision = () => {
    setIsAddDivisionModalOpen(true);
  };

  const handleSaveDivision = async (division: {
    name: string;
    description: string;
    headName: string;
    headTitle: string;
    budget: number;
    vision: string;
    mission: string;
    goals: string[];
  }) => {
    try {
      const newDepartment = await createDepartment(division);
      if (newDepartment) {
        setDepartments(prev => [...prev, newDepartment]);
        
        toast({
          title: "Division Added",
          description: `${division.name} has been added successfully.`,
        });
      }
      setIsAddDivisionModalOpen(false);
    } catch (error) {
      console.error("Error adding division:", error);
      toast({
        title: "Error",
        description: "Failed to add division",
        variant: "destructive",
      });
    }
  };

  const handleEditInfo = (department: Department) => {
    setCurrentDepartment(department);
    setEditedDivision({
      name: department.name,
      description: department.description || "",
      headName: department.headName,
      headTitle: department.headTitle,
      budget: department.budget,
      vision: department.vision || "",
      mission: department.mission || "",
      goals: department.goals && department.goals.length > 0 ? department.goals : [""]
    });
    setIsEditDivisionModalOpen(true);
  };

  const handleAddGoal = () => {
    setEditedDivision({
      ...editedDivision,
      goals: [...editedDivision.goals, ""]
    });
  };

  const handleRemoveGoal = (index: number) => {
    const updatedGoals = [...editedDivision.goals];
    updatedGoals.splice(index, 1);
    setEditedDivision({
      ...editedDivision,
      goals: updatedGoals
    });
  };

  const handleGoalChange = (index: number, value: string) => {
    const updatedGoals = [...editedDivision.goals];
    updatedGoals[index] = value;
    setEditedDivision({
      ...editedDivision,
      goals: updatedGoals
    });
  };

  const handleSaveEditedDivision = async () => {
    if (!currentDepartment) return;
    
    try {
      const filteredGoals = editedDivision.goals.filter(goal => goal.trim() !== "");
      
      await updateDepartment(currentDepartment.id, {
        ...editedDivision,
        goals: filteredGoals
      });
      
      setDepartments(prev => 
        prev.map(dept => 
          dept.id === currentDepartment.id 
            ? { 
                ...dept, 
                name: editedDivision.name,
                description: editedDivision.description,
                headName: editedDivision.headName,
                headTitle: editedDivision.headTitle,
                budget: editedDivision.budget,
                vision: editedDivision.vision,
                mission: editedDivision.mission,
                goals: filteredGoals
              } 
            : dept
        )
      );
      
      toast({
        title: "Division Updated",
        description: `${editedDivision.name} has been updated successfully.`,
      });
      setIsEditDivisionModalOpen(false);
    } catch (error) {
      console.error("Error updating division:", error);
      toast({
        title: "Error",
        description: "Failed to update division",
        variant: "destructive",
      });
    }
  };

  const handleAddEmployee = (department: Department) => {
    setCurrentDepartment(department);
    setIsAddEmployeeModalOpen(true);
  };

  const handleEmployeeAdded = async (employee: any) => {
    if (!currentDepartment) return;
    
    try {
      await createEmployee({
        ...employee,
        departmentId: currentDepartment.id
      });
      
      toast({
        title: "Employee Added",
        description: `${employee.name} has been added to ${currentDepartment.name}.`,
      });
      setIsAddEmployeeModalOpen(false);
    } catch (error) {
      console.error("Error adding employee:", error);
      toast({
        title: "Error",
        description: "Failed to add employee",
        variant: "destructive",
      });
    }
  };

  const handleCreateMFO = (department: Department) => {
    setCurrentDepartment(department);
    setIsCreateMFOModalOpen(true);
  };

  const handleMFOCreated = async (mfo: MFOItem) => {
    if (!currentDepartment) return;
    
    try {
      const mappedMFO = {
        ...mfo,
        departmentId: currentDepartment.id,
        priority: mfo.priority as string,
        status: mfo.status as string,
        progress: mfo.progress || 0
      };
      
      const mfoId = await createMFO(mappedMFO);
      
      setDepartments(prev => 
        prev.map(dept => 
          dept.id === currentDepartment.id 
            ? { ...dept, mfoCount: dept.mfoCount + 1 } 
            : dept
        )
      );
      
      toast({
        title: "MFO Created",
        description: `Successfully created ${mfo.code}: ${mfo.title} for ${currentDepartment.name}.`,
      });
      setIsCreateMFOModalOpen(false);
    } catch (error) {
      console.error("Error creating MFO:", error);
      toast({
        title: "Error",
        description: "Failed to create MFO",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight">Divisions</h2>
        <div className="flex space-x-2">
          <Button onClick={handleAddDivision}>
            <Plus className="mr-2 h-4 w-4" />
            Add Division
          </Button>
        </div>
      </div>

      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search divisions..."
          className="pl-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="transition-all hover:shadow-md">
              <CardHeader className="pb-2">
                <Skeleton className="h-6 w-3/4" />
              </CardHeader>
              <CardContent className="pb-2">
                <Skeleton className="h-4 w-full mb-4" />
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </CardContent>
              <CardFooter className="flex flex-wrap gap-2 pt-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-28" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : filteredDepartments?.length === 0 ? (
        <div className="bg-muted/40 border rounded-lg p-6 flex flex-col items-center justify-center min-h-[200px]">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-xl font-medium mb-2">No divisions found</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
            {searchQuery 
              ? "No divisions match your search criteria." 
              : "There are no divisions in the system yet."}
          </p>
          <Button onClick={handleAddDivision}>
            <Plus className="mr-2 h-4 w-4" />
            Add Division
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDepartments.map((department) => (
            <Card key={department.id} className="transition-all hover:shadow-md">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl font-bold flex items-center">
                    <Building2 className="mr-2 h-5 w-5 text-primary" />
                    {department.name}
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleEditInfo(department)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <p className="text-sm text-muted-foreground mb-4">{department.description}</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Division Head:</span>
                    <span>{department.headName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Budget ({department.year}):</span>
                    <span>₱{department.budget.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">MFOs:</span>
                    <span>{department.mfoCount}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{department.collaborators?.length} Employees</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-wrap gap-2 pt-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCreateMFO(department)}
                >
                  <FilePlus className="mr-2 h-4 w-4" />
                  Add MFO
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleAddEmployee(department)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add Employee
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => navigate(`/dashboard/divisions/${department.id}`)}
                >
                  View Details
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <AddEmployeeModal 
        isOpen={isAddEmployeeModalOpen} 
        onOpenChange={setIsAddEmployeeModalOpen} 
        onSave={handleEmployeeAdded}
      />

      <CreateMFOModal 
        isOpen={isCreateMFOModalOpen}
        onOpenChange={setIsCreateMFOModalOpen}
        departments={departments.map(dept => ({ id: dept.id, name: dept.name }))}
        employees={allEmployees}
        mfoCodes={mfoCodes}
        onMFOCreated={handleMFOCreated}
      />

      <AddDivisionModal
        isOpen={isAddDivisionModalOpen}
        onOpenChange={setIsAddDivisionModalOpen}
        onSave={handleSaveDivision}
      />

      <Dialog open={isEditDivisionModalOpen} onOpenChange={setIsEditDivisionModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Division</DialogTitle>
            <DialogDescription>
              Update the information for {currentDepartment?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Division Name*</Label>
              <Input
                id="edit-name"
                value={editedDivision.name}
                onChange={(e) => setEditedDivision({ ...editedDivision, name: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editedDivision.description}
                onChange={(e) => setEditedDivision({ ...editedDivision, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-headName">Division Head Name*</Label>
                <Input
                  id="edit-headName"
                  value={editedDivision.headName}
                  onChange={(e) => setEditedDivision({ ...editedDivision, headName: e.target.value })}
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="edit-headTitle">Head Title*</Label>
                <Input
                  id="edit-headTitle"
                  value={editedDivision.headTitle}
                  onChange={(e) => setEditedDivision({ ...editedDivision, headTitle: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-budget">Budget (₱)*</Label>
              <Input
                id="edit-budget"
                type="number"
                value={editedDivision.budget || ""}
                onChange={(e) => setEditedDivision({ ...editedDivision, budget: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-vision">Division Vision</Label>
              <Textarea
                id="edit-vision"
                placeholder="Enter division vision statement"
                value={editedDivision.vision}
                onChange={(e) => setEditedDivision({ ...editedDivision, vision: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-mission">Division Mission</Label>
              <Textarea
                id="edit-mission"
                placeholder="Enter division mission statement"
                value={editedDivision.mission}
                onChange={(e) => setEditedDivision({ ...editedDivision, mission: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Strategic Goals</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={handleAddGoal}
                  className="h-8"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Goal
                </Button>
              </div>
              
              <div className="space-y-3">
                {editedDivision.goals.map((goal, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder={`Strategic goal ${index + 1}`}
                      value={goal}
                      onChange={(e) => handleGoalChange(index, e.target.value)}
                    />
                    {editedDivision.goals.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleRemoveGoal(index)}
                        className="h-8 w-8 flex-shrink-0"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSaveEditedDivision}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Departments;
